import React, { useRef, useEffect } from "react";
import { MdMenu, MdClose, MdKeyboardArrowDown, MdChevronRight } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../../assets/logo_rpa2.png";

import { useLanguage } from "../../../contexts/LanguageContext";

const navLinks = [
  { label: "Home", path: "/" },
  {
    label: "About Us",
    path: "/about",
    children: [
      { label: "About Us", path: "/about" },
      { label: "Our Mission and Values", path: "/our-mission-values" },
      { label: "Portfolio", path: "/portfolio" },
      { label: "Our Staff", path: "/team" },
      { label: "Our Board", path: "/our-board" },
      {
        label: "Learn about our work",
        path: "/our-work",
        children: [
          { label: "Learn from China", path: "/view-stories/cina-negeri-yang-petaninya-tak-dibiarkan-berjuang-sendiri" },
        ],
      },
    ],
  },
  { label: "Program", path: "/program" },
  // {
  //   label: "Get Involved",
  //   path: "/get-involved",
  //   children: [
  //     { label: "Membership", path: "/membership" },
  //     { label: "Volunteer", path: "/volunteer" },
  //     { label: "Partner With Us", path: "/partner-with-us" },
  //     { label: "Jobs and Internships", path: "/jobs-internships" },
  //   ],
  // },
  { label: "Inovasi Pangan", path: "/etalase" },
  { label: "Stories", path: "/stories" },
  { label: "Donate", path: "/donate" },
];

const Navbar = () => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState(null);
  const [openNestedDropdown, setOpenNestedDropdown] = React.useState(null);
  const [mobileOpenPath, setMobileOpenPath] = React.useState(null);
  const [mobileOpenNestedPath, setMobileOpenNestedPath] = React.useState(null);
  const [scrolled, setScrolled] = React.useState(false);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const isParentActive = (item) => {
    if (!item.children) return false;
    return item.children.some(
      (child) =>
        location.pathname === child.path ||
        (child.children && child.children.some((gc) => location.pathname === gc.path))
    );
  };

  const toggleLanguage = () =>
    setLanguage((prev) => (prev === "ID" ? "EN" : "ID"));

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
    setOpenDropdown(null);
    setOpenNestedDropdown(null);
    setMobileOpenPath(null);
    setMobileOpenNestedPath(null);
  };

  const handleDesktopDropdown = (path) =>
    setOpenDropdown((prev) => {
      if (prev !== path) setOpenNestedDropdown(null);
      return prev === path ? null : path;
    });

  const handleNestedDropdown = (path) =>
    setOpenNestedDropdown((prev) => (prev === path ? null : path));

  const handleMobileAccordion = (path) => {
    setMobileOpenPath((prev) => (prev === path ? null : path));
    setMobileOpenNestedPath(null);
  };

  const handleMobileNestedAccordion = (path) =>
    setMobileOpenNestedPath((prev) => (prev === path ? null : path));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setOpenNestedDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setOpenNestedDropdown(null);
    setMobileOpenPath(null);
    setMobileOpenNestedPath(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <motion.div
        ref={navRef}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 border-b border-[#e8e0d0] transition-all duration-300 ${
          scrolled
            ? "bg-white/25 backdrop-blur-md shadow-md"
            : "bg-white shadow-sm"
        }`}
      >
        <div className="w-full px-4 flex items-center justify-between py-3 gap-2">

          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer shrink-0"
            onClick={() => handleNavigate("/")}
          >
            <img
              src={Logo}
              alt="Rumoh Pangan Aceh Logo"
              className="w-8 h-8 object-contain"
            />
            <div className="leading-tight">
              <p className="text-sm font-bold text-[#1a1a18] tracking-wide font-serif whitespace-nowrap">
                Rumoh Pangan Aceh
              </p>
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-2 flex-1 justify-center">
            {navLinks.map((item) => {
              const active = isActive(item.path) || isParentActive(item);
              const isThisOpen = openDropdown === item.path;

              if (item.children) {
                return (
                  <div key={item.path} className="relative">
                    <button
                      onClick={() => handleDesktopDropdown(item.path)}
                      className={`relative flex items-center gap-0.5 px-2.5 py-2 text-[13px] font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer group ${
                        active ? "text-[#c9862a]" : "text-[#3a3a34] hover:text-[#c9862a]"
                      }`}
                    >
                      {item.label}
                      <MdKeyboardArrowDown
                        className={`text-sm transition-transform duration-200 ${
                          isThisOpen ? "rotate-180" : ""
                        }`}
                      />
                      <span
                        className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2.5px] rounded-full bg-[#c9862a] transition-all duration-300 ${
                          active
                            ? "w-4/5 opacity-100"
                            : "w-0 opacity-0 group-hover:w-4/5 group-hover:opacity-60"
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {isThisOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="absolute top-full left-0 mt-2 w-52 bg-white border border-[#e8e0d0] shadow-lg rounded-sm overflow-visible z-50"
                        >
                          {item.children.map((child) => {
                            /* ── Child dengan nested submenu ── */
                            if (child.children) {
                              const isNestedOpen = openNestedDropdown === child.path;
                              const nestedActive =
                                isActive(child.path) ||
                                child.children.some((gc) => isActive(gc.path));

                              return (
                                <div key={child.path} className="relative group/nested">
                                  <button
                                    onClick={() => handleNestedDropdown(child.path)}
                                    onMouseEnter={() => setOpenNestedDropdown(child.path)}
                                    onMouseLeave={() => {}}
                                    className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors duration-150 flex items-center justify-between ${
                                      nestedActive
                                        ? "text-[#c9862a] bg-[#faf6ef]"
                                        : "text-[#3a3a34] hover:text-[#c9862a] hover:bg-[#faf6ef]"
                                    }`}
                                  >
                                    {child.label}
                                    <MdChevronRight
                                      className={`text-base flex-shrink-0 transition-transform duration-200 ${
                                        isNestedOpen ? "rotate-90" : ""
                                      }`}
                                    />
                                  </button>

                                  <AnimatePresence>
                                    {isNestedOpen && (
                                      <motion.div
                                        initial={{ opacity: 0, x: -8, scale: 0.97 }}
                                        animate={{ opacity: 1, x: 0, scale: 1 }}
                                        exit={{ opacity: 0, x: -8, scale: 0.97 }}
                                        transition={{ duration: 0.15, ease: "easeOut" }}
                                        onMouseEnter={() => setOpenNestedDropdown(child.path)}
                                        onMouseLeave={() => setOpenNestedDropdown(null)}
                                        className="absolute top-0 left-full ml-1 w-64 bg-white border border-[#e8e0d0] shadow-lg rounded-sm overflow-hidden z-50"
                                      >
                                        {child.children.map((grandchild) => (
                                          <button
                                            key={grandchild.path}
                                            onClick={() => handleNavigate(grandchild.path)}
                                            className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors duration-150 flex items-center justify-between ${
                                              isActive(grandchild.path)
                                                ? "text-[#c9862a] bg-[#faf6ef]"
                                                : "text-[#3a3a34] hover:text-[#c9862a] hover:bg-[#faf6ef]"
                                            }`}
                                          >
                                            {grandchild.label}
                                            {isActive(grandchild.path) && (
                                              <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a] flex-shrink-0" />
                                            )}
                                          </button>
                                        ))}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            }

                            /* ── Child biasa ── */
                            return (
                              <button
                                key={child.path}
                                onClick={() => handleNavigate(child.path)}
                                className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors duration-150 flex items-center justify-between ${
                                  isActive(child.path)
                                    ? "text-[#c9862a] bg-[#faf6ef]"
                                    : "text-[#3a3a34] hover:text-[#c9862a] hover:bg-[#faf6ef]"
                                }`}
                              >
                                {child.label}
                                {isActive(child.path) && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a]" />
                                )}
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  className={`relative px-2.5 py-2 text-[13px] font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer group ${
                    active ? "text-[#c9862a]" : "text-[#3a3a34] hover:text-[#c9862a]"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2.5px] rounded-full bg-[#c9862a] transition-all duration-300 ${
                      active
                        ? "w-4/5 opacity-100"
                        : "w-0 opacity-0 group-hover:w-4/5 group-hover:opacity-60"
                    }`}
                  />
                </button>
              );
            })}
          </nav>

          {/* Kanan: Language + Login (desktop) */}
          <div className="hidden xl:flex items-center gap-2 shrink-0">
            <button
              onClick={toggleLanguage}
              className="text-xs font-bold tracking-widest uppercase px-3 py-2 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              aria-label="Toggle Language"
            >
              [{language}]
            </button>
            <button
              onClick={() => handleNavigate("/login")}
              className="bg-[#c9862a] hover:bg-[#e09b3a] text-white text-xs font-bold tracking-widest uppercase px-5 py-2.5 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Login
            </button>
          </div>

          {/* Hamburger */}
          <div className="xl:hidden shrink-0">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="bg-[#faf6ef] p-2 border border-[#e8e0d0] hover:bg-[#f0e8d8] transition-colors duration-200"
            >
              {isOpen ? (
                <MdClose className="text-[#c9862a] text-2xl" />
              ) : (
                <MdMenu className="text-[#c9862a] text-2xl" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="xl:hidden bg-white border-t border-[#e8e0d0] overflow-hidden"
            >
              <div className="px-4 py-4 space-y-1">
                {navLinks.map((item) => {
                  const active = isActive(item.path) || isParentActive(item);
                  const isThisAccordionOpen = mobileOpenPath === item.path;

                  if (item.children) {
                    return (
                      <div key={item.path}>
                        <button
                          onClick={() => handleMobileAccordion(item.path)}
                          className={`flex items-center justify-between w-full text-left text-sm font-medium py-3 px-3 transition-colors duration-200 ${
                            active
                              ? "text-[#c9862a] bg-[#faf6ef] border-l-[3px] border-[#c9862a]"
                              : "text-[#3a3a34] hover:text-[#c9862a] hover:bg-[#faf6ef]"
                          }`}
                        >
                          {item.label}
                          <MdKeyboardArrowDown
                            className={`text-lg transition-transform duration-200 ${
                              isThisAccordionOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        <AnimatePresence>
                          {isThisAccordionOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden bg-[#faf6ef] border-l-[3px] border-[#e8e0d0] ml-3"
                            >
                              {item.children.map((child) => {
                                /* ── Mobile child dengan nested ── */
                                if (child.children) {
                                  const isNestedOpen = mobileOpenNestedPath === child.path;
                                  const nestedActive =
                                    isActive(child.path) ||
                                    child.children.some((gc) => isActive(gc.path));

                                  return (
                                    <div key={child.path}>
                                      <button
                                        onClick={() => handleMobileNestedAccordion(child.path)}
                                        className={`flex items-center justify-between w-full text-left text-sm py-3 px-4 transition-colors duration-200 ${
                                          nestedActive
                                            ? "text-[#c9862a] font-bold border-l-[3px] border-[#c9862a] bg-[#f0e8d8]"
                                            : "text-[#6b6456] hover:text-[#c9862a] font-medium"
                                        }`}
                                      >
                                        {child.label}
                                        <MdKeyboardArrowDown
                                          className={`text-base flex-shrink-0 transition-transform duration-200 ${
                                            isNestedOpen ? "rotate-180" : ""
                                          }`}
                                        />
                                      </button>

                                      <AnimatePresence>
                                        {isNestedOpen && (
                                          <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.18 }}
                                            className="overflow-hidden bg-[#f0e8d8] border-l-[3px] border-[#c9862a] ml-4"
                                          >
                                            {child.children.map((grandchild) => (
                                              <button
                                                key={grandchild.path}
                                                onClick={() => handleNavigate(grandchild.path)}
                                                className={`flex items-center justify-between w-full text-left text-sm py-3 px-4 transition-colors duration-200 ${
                                                  isActive(grandchild.path)
                                                    ? "text-[#c9862a] font-bold"
                                                    : "text-[#6b6456] hover:text-[#c9862a] font-medium"
                                                }`}
                                              >
                                                {grandchild.label}
                                                {isActive(grandchild.path) && (
                                                  <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a] flex-shrink-0" />
                                                )}
                                              </button>
                                            ))}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                }

                                /* ── Mobile child biasa ── */
                                return (
                                  <button
                                    key={child.path}
                                    onClick={() => handleNavigate(child.path)}
                                    className={`flex items-center justify-between w-full text-left text-sm py-3 px-4 transition-colors duration-200 ${
                                      isActive(child.path)
                                        ? "text-[#c9862a] font-bold border-l-[3px] border-[#c9862a] bg-[#f0e8d8]"
                                        : "text-[#6b6456] hover:text-[#c9862a] font-medium"
                                    }`}
                                  >
                                    {child.label}
                                    {isActive(child.path) && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a]" />
                                    )}
                                  </button>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className={`flex items-center justify-between w-full text-left text-sm font-medium py-3 px-3 transition-colors duration-200 ${
                        isActive(item.path)
                          ? "text-[#c9862a] bg-[#faf6ef] border-l-[3px] border-[#c9862a]"
                          : "text-[#3a3a34] hover:text-[#c9862a] hover:bg-[#faf6ef]"
                      }`}
                    >
                      {item.label}
                      {isActive(item.path) && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#c9862a]" />
                      )}
                    </button>
                  );
                })}

                {/* Language + Login di mobile */}
                <div className="pt-3 flex gap-2">
                  <button
                    onClick={toggleLanguage}
                    className="flex-1 border border-[#c9862a] text-[#c9862a] text-sm font-bold tracking-widest uppercase py-2.5 transition-colors duration-200"
                  >
                    [{language}]
                  </button>
                  <button
                    onClick={() => handleNavigate("/login")}
                    className="flex-1 bg-[#c9862a] hover:bg-[#e09b3a] text-white text-sm font-bold tracking-widest uppercase py-2.5 transition-colors duration-200"
                  >
                    Login
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Spacer */}
      <div className="h-[57px]" />
    </>
  );
};

export default Navbar;